import {autocurry, compose, curry, repeatAdditiv, rightCurry} from "./functionalTools.js";
export class TextPrinter {
    public static join = autocurry(
        (glue, val, ...args)=>["string", "number"].indexOf(typeof(val)) > -1 ?
            [val].concat(args).join(glue) :
            typeof(val) === "undefined" || val === null ?
                "" :
                val.concat(args).join(glue)
    );
    public static sentence = TextPrinter.join(" ");
    public static list = TextPrinter.join(", ");
    public static joinarguments = (str, array, glue="\n")=>[str].concat(array).join(glue);
    public static append = autocurry((suffix, str)=>(str ? str : "") + suffix);
    public static prepend = autocurry((prefix, str)=>prefix + (str ? str : ""));
    public static brace = autocurry((str: string, pre: string, post: string) => pre + str + post);
    public static roundbracket = rightCurry(TextPrinter.brace,  "(", ")");
    public static squarebracket = rightCurry(TextPrinter.brace,  "[", "]");
}

export class HTMLPrinter extends TextPrinter {
    public static argumentJoiner = (glue, func) => compose(func, HTMLPrinter.join(glue));
    public static argsToLineConverter = curry(HTMLPrinter.argumentJoiner, " ");
    public static argsToBlockConverter = curry(HTMLPrinter.argumentJoiner, "\n");
    public static encloser = (pre, post) => rightCurry(HTMLPrinter.brace, pre, post);
    public static asLineEncloser = (pre, post) => HTMLPrinter.argsToLineConverter(HTMLPrinter.encloser(pre, post));
    public static asBlockEncloser = (pre, post) => HTMLPrinter.argsToBlockConverter(HTMLPrinter.encloser(pre, post));
    public static getWords = x=>x.split(" ");
    public static firstWordProvider = x => HTMLPrinter.getWords(x)[0];
    public static collection = HTMLPrinter.join("\n");
    public static readonly = "readonly";
    public static prop = autocurry(
        (propname, value, ...restargs) => [propname, HTMLPrinter.quote([value, ...restargs].join(" ").trim())].join("="));
    public static style = (value, ...restargs) => ["style", HTMLPrinter.quote([value, ...restargs].join("; ").trim())].join("=");

    public static CSSprop = autocurry(
        (propname, value, ...restargs) => [propname, [value, ...restargs].join(" ").trim()].join(":"));
    public static quote = HTMLPrinter.encloser("\"", "\"");
    public static quoteSingle = HTMLPrinter.encloser("'", "'");
    public static cssClass = HTMLPrinter.prop("class");
    public static toolTip = HTMLPrinter.prop("title");
    public static type = HTMLPrinter.prop("type");
    public static id = HTMLPrinter.prop("id");
    public static propName = HTMLPrinter.prop("name");
    public static value = HTMLPrinter.prop("value");
    public static for = HTMLPrinter.prop("for");
    public static href = HTMLPrinter.prop("href");
    public static emptyhref = HTMLPrinter.href("#");
    public static src = HTMLPrinter.prop("src");
    public static alt = HTMLPrinter.prop("alt");
    public static step = HTMLPrinter.prop("step");
    public static width = HTMLPrinter.prop("width");
    public static size = HTMLPrinter.prop("size");
    public static rel = HTMLPrinter.prop("rel");
    public static px = HTMLPrinter.append("px");
    public static colspan = HTMLPrinter.prop("colspan");
    public static target = HTMLPrinter.prop("target");
    public static targetNewTag = HTMLPrinter.target("_blank");
    public static ondblclick = HTMLPrinter.prop("ondblclick");
    public static onclick=HTMLPrinter.prop("onclick");
    public static dataProp = compose(HTMLPrinter.prop, HTMLPrinter.prepend("data-"));
    public static addSemicolon = HTMLPrinter.append(";");
    public static classSelector = HTMLPrinter.prepend(".");
    public static nthChildSelector = compose(HTMLPrinter.prepend(":nth-child"), HTMLPrinter.roundbracket);
    public static idSelector = HTMLPrinter.prepend("#");
    public static removeSpaces = compose(HTMLPrinter.join(""), HTMLPrinter.getWords);
    public static trim = x=>x.trim();
    public static percent = HTMLPrinter.append("%");
    public static startTag = HTMLPrinter.asLineEncloser("<", ">");
    public static endtag = compose(
        HTMLPrinter.asLineEncloser("</", ">"),
        HTMLPrinter.firstWordProvider,
        HTMLPrinter.join(" ")
    );
    public static selfClosingTag = autocurry(HTMLPrinter.asLineEncloser("<", "/>"));

    public static inputTag = curry(HTMLPrinter.startTag, "input");
    public static inTagWrapper = tagname => HTMLPrinter.asBlockEncloser(HTMLPrinter.startTag(tagname), HTMLPrinter.endtag(tagname));
    public static inTagWithPropsWrapper = compose(HTMLPrinter.inTagWrapper, HTMLPrinter.join(" "));
    public static tagWithPropsWrapper = x=>compose(
        HTMLPrinter.inTagWrapper,
        HTMLPrinter.prepend(
            HTMLPrinter.append(" ", x)
        ),
        HTMLPrinter.join(" ")
    );
    public static labelWithProps = HTMLPrinter.tagWithPropsWrapper("label");
    public static label = HTMLPrinter.inTagWrapper("label");
    public static divWitHProps = HTMLPrinter.tagWithPropsWrapper("div");
    public static span = HTMLPrinter.inTagWrapper("span");
    public static spanWithProps = HTMLPrinter.tagWithPropsWrapper("span");
    public static p = HTMLPrinter.inTagWrapper("p");
    public static pWithProps = HTMLPrinter.tagWithPropsWrapper("p");
    public static a = HTMLPrinter.tagWithPropsWrapper("a");
    public static s = HTMLPrinter.inTagWrapper("s");
    public static sWithProps = HTMLPrinter.tagWithPropsWrapper("s");
    public static h1= HTMLPrinter.inTagWrapper("h1");
    public static h2 = HTMLPrinter.inTagWrapper("h2");
    public static h2WithProps = HTMLPrinter.tagWithPropsWrapper("h2");
    public static h3 = HTMLPrinter.inTagWrapper("h3");
    public static strong = HTMLPrinter.inTagWrapper("strong");
    public static strongWithProps = HTMLPrinter.tagWithPropsWrapper("strong");
    public static img = curry(HTMLPrinter.selfClosingTag, "img");
    public static br = HTMLPrinter.selfClosingTag("br");
    public static tr = HTMLPrinter.inTagWrapper("tr");
    public static trWithProps = HTMLPrinter.tagWithPropsWrapper("tr");
    public static td = HTMLPrinter.inTagWrapper("td");
    public static tdWithProps = HTMLPrinter.tagWithPropsWrapper("td");
    // public static ulWithProps = HTMLPrinter.tagWithPropsWrapper("ul");
    public static button = HTMLPrinter.inTagWrapper("button");
    public static buttonWithProps = HTMLPrinter.tagWithPropsWrapper("button");
    public static ul = HTMLPrinter.inTagWrapper("ul");
    public static ulWithProps = HTMLPrinter.tagWithPropsWrapper("ul");
    public static li = HTMLPrinter.inTagWrapper("li");
    public static select = HTMLPrinter.inTagWrapper("select");
    public static selectWithProps = HTMLPrinter.tagWithPropsWrapper("select");
    public static option = HTMLPrinter.tagWithPropsWrapper("option");
    public static liWithProps = HTMLPrinter.tagWithPropsWrapper("li");
    public static sectionWithProps = HTMLPrinter.tagWithPropsWrapper("section");
    public static div = compose(HTMLPrinter.inTagWithPropsWrapper, HTMLPrinter.prepend("div "));
    public static th = HTMLPrinter.inTagWrapper("th");
    public static thWithProps = HTMLPrinter.tagWithPropsWrapper("th");
    public static textArea = HTMLPrinter.inTagWrapper("textarea");
    public static textAreaWithProps = HTMLPrinter.tagWithPropsWrapper("textarea");
    public static loadingDots = ()=>HTMLPrinter.pWithProps(HTMLPrinter.cssClass("loadingdots"))(repeatAdditiv(3)(HTMLPrinter.span, "."));
    public static getLocale = (lang, land)=>[lang, land.toUpperCase()].join("-");
    public static toDomElement = function(htmlString) {
        const div = document.createElement("div");
        div.innerHTML = htmlString.trim();
        return div.firstChild as Node;
    };
    public static escapeHTML = s=>{
        return s.replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#39;");
    }
    public static urlparam =(key, value)=>HTMLPrinter.brace("=", encodeURIComponent(key), encodeURIComponent(value));
    static nbsp = "&nbsp;";
    public static srcset =  (urltimes1:string, urltimes1point5:string, urltimes2:string)=>HTMLPrinter.prop("srcset")([urltimes1+ " 1x", urltimes1point5+" 1.5x", urltimes2+" 2x"].join(", "));
    public static getURLParamString(paramObject) {
        const parts = [] as string[];
        for(const key in paramObject) {
            parts.push(HTMLPrinter.urlparam(key, paramObject[key]));
        }
        return "?" + parts.join("&");
    }
}
